import React from 'react';

export default function VerticalColorbarLegend({
    metric, // "co" | "temp" | "vis"
    minV,
    maxV, // 수치범위
    threshold, // 임계값(옵션)
    unit = '',
    width = 16, // 바 너비
    height = 160, // 바 높이
    viridis,
}) {
    const wrapRef = React.useRef(null);
    const canvasRef = React.useRef(null);

    const invert = metric === 'vis'; // 가시거리만 색 방향 반전
    const colorMin = invert ? '#b91c1c' : '#505050ff';
    const colorMax = invert ? '#505050ff' : '#b91c1c';

    React.useEffect(() => {
        const wrap = wrapRef.current;
        const cvs = canvasRef.current;
        if (!wrap || !cvs || typeof viridis !== 'function') return;

        let raf = 0;
        const draw = () => {
            const W = Math.max(8, width);
            const H = Math.max(60, height);
            // 실제 캔버스 픽셀 크기
            cvs.width = W;
            cvs.height = H;

            const ctx = cvs.getContext('2d');

            // ⬆︎ 세로 그라데이션 (아래→위). invert면 색 순서 반전
            // createLinearGradient(x0,y0,x1,y1) : (0,H) -> (0,0) = 아래에서 위로
            const grad = ctx.createLinearGradient(0, H, 0, 0);
            const stops = 32;
            for (let i = 0; i <= stops; i++) {
                let t = i / stops; // 0=bottom, 1=top
                if (invert) t = 1 - t; // vis는 반대
                const [r, g, b] = viridis(t);
                grad.addColorStop(i / stops, `rgb(${r},${g},${b})`);
            }
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, W, H);

            // 테두리
            ctx.strokeStyle = '#cbd5e1';
            ctx.lineWidth = 1;
            ctx.strokeRect(0.5, 0.5, W - 1, H - 1);

            // threshold 표시 (가로선)
            if (Number.isFinite(threshold) && maxV > minV) {
                const y = Math.round(H - ((threshold - minV) / (maxV - minV)) * (H - 1));
                ctx.beginPath();
                ctx.moveTo(-2, y + 0.5);
                ctx.lineTo(W + 2, y + 0.5);
                ctx.lineWidth = 2;
                ctx.strokeStyle = '#ef4444';
                ctx.stroke();
            }
        };

        const ro = new ResizeObserver(() => {
            if (raf) cancelAnimationFrame(raf);
            raf = requestAnimationFrame(draw); // RAF 디바운스
        });
        ro.observe(wrap);
        raf = requestAnimationFrame(draw);

        return () => {
            ro.disconnect();
            if (raf) cancelAnimationFrame(raf);
        };
    }, [metric, minV, maxV, threshold, width, height, invert]);

    const fmt = (v) => `${v}${unit ? ' ' + unit : ''}`;

    return (
        <div ref={wrapRef} className="vlegend">
            {/* 상단: 최대값 */}
            <div className="vlegend__label" style={{ color: colorMax }}>
                <b>{fmt(maxV)}</b>
            </div>

            {/* 세로 바 */}
            <canvas ref={canvasRef} style={{ width: `${width}px`, height: `${height}px`, display: 'block' }} />

            {/* 하단: 최소값 + (선택) threshold 텍스트 */}
            <div className="vlegend__bottom">
                <div className="vlegend__label" style={{ color: colorMin }}>
                    <b>{fmt(minV)}</b>
                </div>
            </div>

            <style>{`
        .vlegend {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 10px 6px 4px;
          background: rgba(255,255,255,0);
          border: 0px solid #e2e8f0;
          border-radius: 10px;
          box-shadow: 0 1px 2px rgba(0,0,0,.04);
          user-select: none;
        }
        .vlegend__label {
          font-size: 12px;
          background:#fff;
          border:1px solid #e2e8f0;
          padding: 2px 6px;
          border-radius: 8px;
          white-space: nowrap;
          box-shadow: 0 1px 2px rgba(0,0,0,.04);
        }
        .vlegend__bottom {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }
        .vlegend__thr {
          font-size: 12px;
          color:#475569;
        }
      `}</style>
        </div>
    );
}

export function HorizontalColorbarLegend({
    metric, // "co" | "temp" | "vis"
    minV,
    maxV, // 범위
    threshold, // 한계치 (optional)
    unit = '',
    height = 22, // 바 높이 (가독성 위해 20~24 추천)
    viridis,
}) {
    const wrapRef = React.useRef(null);
    const canvasRef = React.useRef(null);

    // vis는 낮을수록 위험 → 색 반전
    const invert = metric === 'vis';
    const colorMin = invert ? '#b91c1c' : '#505050ff';
    const colorMax = invert ? '#505050ff' : '#b91c1c';

    // 부모 폭에 맞춰 캔버스 그리기
    React.useEffect(() => {
        const wrap = wrapRef.current;
        const cvs = canvasRef.current;
        if (!wrap || !cvs) return;

        let raf = 0;
        const draw = () => {
            const W = Math.max(100, Math.floor(wrap.clientWidth)); // 최소 100px
            const H = height;
            cvs.width = W;
            cvs.height = H;

            const ctx = cvs.getContext('2d');

            // --- 수평 그라데이션 (viridis 샘플링) ---
            const grad = ctx.createLinearGradient(0, 0, W, 0); // x축 방향
            const stops = 32; // 색 스톱 수 (16~64 사이 적당히)
            for (let i = 0; i <= stops; i++) {
                let t = i / stops;
                if (invert) t = 1 - t;
                const [r, g, b] = viridis(t);
                grad.addColorStop(i / stops, `rgb(${r},${g},${b})`);
            }
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, W, H);

            // 테두리
            ctx.strokeStyle = '#cbd5e1';
            ctx.lineWidth = 1;
            ctx.strokeRect(0.5, 0.5, W - 1, H - 1);

            // 한계치 마커 (옵션)
            if (Number.isFinite(threshold) && maxV > minV) {
                const x = Math.round(((threshold - minV) / (maxV - minV)) * (W - 1));
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(x + 0.5, -2);
                ctx.lineTo(x + 0.5, H + 2);
                ctx.lineWidth = 2;
                ctx.strokeStyle = '#ef4444';
                ctx.stroke();
                ctx.restore();
            }
        };

        const schedule = () => {
            if (raf) cancelAnimationFrame(raf);
            raf = requestAnimationFrame(draw);
        };

        const ro = new ResizeObserver(schedule);
        ro.observe(wrap);

        schedule();

        return () => {
            ro.disconnect();
            if (raf) cancelAnimationFrame(raf);
        };
    }, [height, minV, maxV, threshold, invert, unit, metric]);

    // 포맷
    const fmt = React.useCallback((v) => `${v}${unit ? ' ' + unit : ''}`, [unit]);

    return (
        <div ref={wrapRef} className="legend2" style={{ width: '40%' }}>
            <div className="legend2__labels">
                <div className="legend2__label legend2__label--min">
                    <b style={{ color: colorMin }}>{fmt(minV)}</b>
                </div>
                <canvas ref={canvasRef} style={{ flex: 1, height: `${height}px`, display: 'block' }} />
                {Number.isFinite(maxV) && (
                    <div className="legend2__label legend2__label--max">
                        <b style={{ color: colorMax }}>{fmt(maxV)}</b>
                    </div>
                )}
            </div>
            <style>{`
        .legend2__labels{
          display:flex; align-items:center; justify-content:center;
          margin-top:2px; gap:5px;
        }
        .legend2__label{
          font-size:12px; color:#475569;
          background:#fff; padding:2px 6px; border:1px solid #e2e8f0;
          border-radius:8px; white-space:nowrap;
          box-shadow: 0 1px 2px rgba(0,0,0,.04);
        }
        .legend2__note{ margin-top:4px; font-size:12px; }
      `}</style>
        </div>
    );
}
